import { useState, useMemo } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { Menu, X, ShoppingCart, BookOpen } from 'lucide-react'
import { useQuoteBasketStore } from '@/stores/quoteBasketStore'
import { usePageVisibility } from '@/hooks/useSiteSettings'
import { cn } from '@/lib/utils'

const allNavigation = [
  { name: 'Home', href: '/', key: 'home' },
  { name: 'About Us', href: '/about', key: 'about' },
  { name: 'Sustainability', href: '/sustainability', key: 'sustainability' },
  { name: 'Certificate', href: '/certificates', key: 'certificates' },
  { name: 'Products', href: '/products', key: 'products' },
  { name: 'Gallery', href: '/gallery', key: 'gallery' },
  { name: 'Contact', href: '/contact', key: 'contact' },
]

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const itemCount = useQuoteBasketStore((state) => state.getItemCount())
  const { data: pageVisibility } = usePageVisibility()

  // Filter navigation based on page visibility
  const navigation = useMemo(() => {
    return allNavigation.filter((item) => {
      if (item.key === 'home') return true // Home is always visible
      if (!pageVisibility) return true // Show all if settings not loaded
      return pageVisibility[item.key as keyof typeof pageVisibility] !== false
    })
  }, [pageVisibility])

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm">
      <div className="container-custom">
        <div className="flex items-center justify-between h-24 lg:h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/images/logo.png"
              alt="Engitronix"
              className="h-16 lg:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    isActive
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
                  )
                }
              >
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Request Catalog */}
            <Link
              to="/catalog-request"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
            >
              <BookOpen className="w-4 h-4" />
              Request Catalog
            </Link>

            {/* Quote Basket */}
            <Link
              to="/quote-basket"
              className="relative p-2 text-secondary-600 hover:text-primary-600 transition-colors"
            >
              <ShoppingCart className="w-6 h-6" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-primary-600 text-white text-xs font-bold rounded-full">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-secondary-600 hover:text-secondary-900"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-secondary-100 bg-white">
          <nav className="container-custom py-4 flex flex-col gap-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'px-4 py-3 text-base font-medium rounded-lg transition-colors',
                    isActive
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
                  )
                }
              >
                {item.name}
              </NavLink>
            ))}
            <Link
              to="/catalog-request"
              onClick={() => setMobileMenuOpen(false)}
              className="mx-4 mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 text-base font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <BookOpen className="w-5 h-5" />
              Request Catalog
            </Link>
          </nav>
        </div>
      )}
    </header>
  )
}
